import { CampaignIdSchema as A, AdGroupIdSchema as c } from "@hprofits/shared";
import { PrivilegeEnum as e } from "@hprofits/shared/auth";
import { z as u } from "zod";
import "../common/acl-view.js";
import { ocPrivate as t } from "../common/auth/procedure-builders.js";
import { createCreationResultSchema as d } from "../common/creation-result.js";
import "../common/owner.js";
import { AdGroupIdParamSchema as n, PricingRuleIdParamSchema as o, ForceActionParamSchema as g, CampaignGroupIdParamSchema as p } from "../common/params.js";
import "../common/errors/constants.js";
import { inUseError as i, notFoundError as r, fieldCannotBeUpdatedError as I, expectationFailedError as s, alreadyExistsError as m } from "../common/errors/contract.js";
import "../common/errors/schemas.js";
import "../swagger/security.js";
import { SwaggerTags as a } from "../swagger/tags.js";
import { PricingRuleCommonInputSchema as l, PricingRuleAdminInputSchema as S, PricingRuleForcedDeletionsSchema as h, PricingRuleStatusChangeCommonSchema as G, PricingRuleStatusChangeAdminSchema as R, PricingRuleBidRangeQuerySchema as P, PricingRuleBidRangeSchema as f, PricingRuleOutputSchema as v, RenameAdGroupSchema as y, UpdateAdGroupSchema as U, CopyAdGroupSchema as C, CreateAdGroupSchema as O, AdGroupSchema as w, AdGroupListSchema as D } from "./schemas.js";
const x = u.union([
  l.unwrap().extend({
    adGroupId: n.shape.adGroupId
  }),
  S.unwrap().extend({
    adGroupId: n.shape.adGroupId
  })
]), E = u.union([
  l.unwrap().extend({
    ruleId: o.shape.ruleId,
    ...h.shape
  }),
  S.unwrap().extend({
    ruleId: o.shape.ruleId,
    ...h.shape
  })
]), T = u.union([
  G.unwrap().extend({
    ruleId: o.shape.ruleId
  }),
  R.unwrap().extend({
    ruleId: o.shape.ruleId
  })
]), K = {
  adGroup: {
    /**
     * Get all ad groups for a campaign
     */
    findAll: t([e.ReadAdvert]).route({
      method: "GET",
      path: "/campaigns/{campaignId}/ad-groups",
      summary: "Get all ad groups for a campaign",
      tags: [a.AD_GROUPS.name]
    }).input(p).output(D).errors({
      ...r("Campaign not found")
    }),
    /**
     * Get an ad group by ID
     */
    findOne: t([e.ReadAdvert]).route({
      method: "GET",
      path: "/ad-groups/{adGroupId}",
      summary: "Get an ad group by ID",
      tags: [a.AD_GROUPS.name]
    }).input(n).output(w).errors({
      ...r("Ad group not found")
    }),
    /**
     * Create a new ad group.
     *
     * Note on input shape: each input below is `Schema.unwrap().extend({...paramShape})`
     * instead of `paramSchema.and(Schema.unwrap())`. `.and()` produces a `ZodIntersection`
     * which swallows the `unknownKeys: 'strict'` signal of the body — `.strict()` becomes
     * a no-op at the contract boundary. `.extend()` preserves strictness.
     */
    create: t([e.WriteAdvert]).route({
      method: "POST",
      path: "/campaigns/{campaignId}/ad-groups",
      summary: "Create an ad group",
      tags: [a.AD_GROUPS.name],
      successStatus: 201
    }).input(
      O.unwrap().extend({
        campaignId: p.shape.campaignId
      })
    ).output(d(c)).errors({
      ...r("Referenced entity in body not found"),
      ...m("Ad group with this name already exists")
    }),
    /**
     * Copy an existing ad group within the same campaign.
     *
     * Modelled as a POST to a `copies` sub-collection rather than a
     * `/{id}/duplicate` verb path: the source is a plain input field, which
     * leaves room for copying across campaigns without another route.
     */
    copy: t([e.WriteAdvert]).route({
      method: "POST",
      path: "/campaigns/{campaignId}/ad-groups/copies",
      summary: "Copy an ad group within a campaign",
      tags: [a.AD_GROUPS.name],
      successStatus: 201
    }).input(
      C.unwrap().extend({
        campaignId: p.shape.campaignId
      })
    ).output(d(c)).errors({
      ...r("Campaign, source ad group, or creative group not found"),
      ...m("Ad group with this name already exists"),
      ...s("Source rule configuration is not compatible with the selected creative group")
    }),
    /**
     * Update an ad group (status only).
     *
     * TODO(#782): rewire when status toggle lands — currently orphaned;
     * kept for forward compatibility.
     */
    update: t([e.WriteAdvert]).route({
      method: "PUT",
      path: "/ad-groups/{adGroupId}",
      summary: "Update an ad group",
      tags: [a.AD_GROUPS.name],
      successStatus: 204
    }).input(
      U.unwrap().extend({
        adGroupId: n.shape.adGroupId
      })
    ).output(u.void()).errors({
      ...r("Ad group or referenced entity not found")
    }),
    /**
     * Rename an ad group
     */
    rename: t([e.WriteAdvert]).route({
      method: "PATCH",
      path: "/ad-groups/{adGroupId}",
      summary: "Rename an ad group",
      tags: [a.AD_GROUPS.name],
      successStatus: 204
    }).input(
      y.unwrap().extend({
        adGroupId: n.shape.adGroupId
      })
    ).output(u.void()).errors({
      ...r("Ad group not found"),
      ...m("Ad group with this name already exists")
    }),
    /**
     * Delete an ad group
     */
    remove: t([e.WriteAdvert]).route({
      method: "DELETE",
      path: "/ad-groups/{adGroupId}",
      summary: "Delete an ad group",
      tags: [a.AD_GROUPS.name],
      successStatus: 204,
      inputStructure: "detailed"
    }).input(
      u.object({
        params: n,
        query: g
      })
    ).output(u.void()).errors({
      ...r("Ad group not found"),
      ...i("Ad group is in use and cannot be deleted")
    })
  },
  pricingRule: {
    /**
     * Create campaign (pricing rule) with variations from target and creative groups
     */
    create: t([e.WriteAdvert]).route({
      method: "POST",
      path: "/ad-groups/{adGroupId}/rules",
      summary: "Add a pricing rule to the ad group.",
      tags: [a.AD_GROUPS.name],
      successStatus: 201
    }).input(x).output(d(A)).errors({
      ...r("Ad group not found"),
      ...s("Status or configuration requirements not met")
    }),
    findOne: t([e.ReadAdvert]).route({
      method: "GET",
      path: "/rules/{ruleId}",
      summary: "Update the pricing rule.",
      tags: [a.AD_GROUPS.name],
      successStatus: 200
    }).input(o).output(v).errors({
      ...r("Rule not found")
    }),
    update: t([e.WriteAdvert]).route({
      method: "PUT",
      path: "/rules/{ruleId}",
      summary: "Update the pricing rule.",
      tags: [a.AD_GROUPS.name],
      successStatus: 204
    }).input(E).output(u.void()).errors({
      ...r("Rule not found"),
      ...s("Status or configuration requirements not met"),
      ...I("Immutable field cannot be updated"),
      // A save that drops a still-bidding sub-rule without naming it in `forceDeleteIds`.
      // Routine, not exceptional — the frontend keys a user-facing message off this code.
      ...i("Pricing rule has running sub-rules this save would delete")
    }),
    bidRange: t([e.ReadAdvert]).route({
      method: "GET",
      path: "/rules/{ruleId}/bid-range",
      summary: "Get the CPM range and the traffic prediction of the pricing rule targeting at the given bid.",
      tags: [a.AD_GROUPS.name],
      successStatus: 200
    }).input(o.extend(P.unwrap().shape)).output(f).errors({
      ...r("Rule not found")
    }),
    updateStatus: t([e.WriteAdvert]).route({
      method: "PUT",
      path: "/rules/{ruleId}/status",
      summary: "Update the pricing rule status (start/stop).",
      tags: [a.AD_GROUPS.name],
      successStatus: 204
    }).input(T).output(u.void()).errors({
      ...r("Rule not found")
    }),
    remove: t([e.WriteAdvert]).route({
      method: "DELETE",
      path: "/rules/{ruleId}",
      summary: "Remove the pricing rule.",
      tags: [a.AD_GROUPS.name],
      successStatus: 204,
      inputStructure: "detailed"
    }).input(
      u.object({
        params: o,
        query: g
      })
    ).output(u.void()).errors({
      ...r("Rule not found"),
      ...i("Rule has active campaigns")
      // todo: ???
    })
  }
};
export {
  K as adGroupContract
};
//# sourceMappingURL=contract.js.map
